import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Dialog,
  Modal as RACModal,
  ModalOverlay,
  SearchField,
  Input,
  Button as RACButton,
} from "react-aria-components";
import {
  AppError,
  Button,
  EmptyState,
  Loader,
  Select,
  Tag,
  TextInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  toast,
} from "@bfc-software/pallet";
import { TFunction } from "i18next";
import {
  Check,
  Edit2,
  Grid,
  Printer,
  Search as SearchIcon,
  X,
} from "react-feather";
import { AuthContext, AuthUtils, logger } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/aaa-api-client";
import { apiClient as issApiClient } from "@bfc-software/bfc-web-ui/lib/iss-api-client";
import { rawFetchClient as orgCfgFetchClient } from "@bfc-software/bfc-web-ui/lib/orgcfg-api-client";
import { DateValue } from "@bfc-software/bfc-web-ui/ui/shared/date-value";
import palletOverrides from "@bfc-software/bfc-web-ui/pallet-overrides.module.css";

import styles from "./selectors.module.css";

/**
 * Matches `selectorAppName` in nextgen_config's selectorsHandler.go -- app
 * users are shared across every app on a warehouse, and this is what scopes
 * the list to the ones that sign in on the floor.
 */
const APP_NAME = "select";

const PIN_LENGTH = 6;

const AVATAR_COLORS = [
  "#2f6fb2",
  "#c47f12",
  "#0aa3a0",
  "#7b61c9",
  "#cf2b22",
  "#3d8b3d",
];

/**
 * Option values double as what gets persisted to `/orgs/user_settings/add` --
 * org_config.yaml defines the `settings` map as free-form strings with no
 * declared value enum, so these are this page's own canonical values rather
 * than anything the API prescribes.
 */
const TASK_TYPE_OPTIONS = ["All", "Runner only", "Regular only"];
const BAND_OPTIONS = ["B1", "B2", "B3", "B4", "B5"];
const VALIDATION_OPTIONS = ["Standard", "Enhanced"];
const ASSIGNMENT_OPTIONS = ["System-assigned", "Manager-override"];

/** The table's own per-value color for the Validation badge -- matches the design's neutral "Standard" / violet "Enhanced" shading. */
const VALIDATION_COLORS: Record<string, string> = {
  Standard: "#6b7280",
  Enhanced: "#7b61c9",
};

function validationColor(value: string): string {
  return VALIDATION_COLORS[value] ?? VALIDATION_COLORS.Standard!;
}

const SUBSTITUTION_OPTIONS = ["No", "Yes"];

/**
 * The `zone_eligibility` key of the `selectorSettings` enum (org_config.yaml)
 * -- unlike the other settings keys, its value is a set (a selector can be
 * eligible for more than one zone), so it's stored as a comma-joined list of
 * zone names rather than one of a fixed set of single values.
 */
const ZONE_ELIGIBILITY_KEY = "zone_eligibility";

function encodeZoneEligibility(zones: string[]): string {
  return zones.join(",");
}

function decodeZoneEligibility(value: string | undefined): string[] {
  return value ? value.split(",").filter(Boolean) : [];
}

function selectItems(options: string[]): Array<{ id: string; label: string }> {
  return options.map((option) => ({ id: option, label: option }));
}

/** The zone-eligibility multi-select -- shared by the create form and the edit drawer. */
function ZonePillPicker({
  zoneNames,
  selected,
  onToggle,
  label,
  emptyLabel,
}: {
  zoneNames: string[];
  selected: string[];
  onToggle: (zoneName: string) => void;
  label: string;
  emptyLabel: string;
}) {
  return (
    <div>
      <span className={styles.fieldLabel}>{label}</span>
      <div className={styles.pillRow}>
        {zoneNames.map((zoneName) => {
          const isSelected = selected.includes(zoneName);
          return (
            <button
              type="button"
              key={zoneName}
              className={isSelected ? styles.pillSelected : styles.pill}
              aria-pressed={isSelected}
              onClick={() => onToggle(zoneName)}
            >
              <span className={styles.pillCheck}>
                {isSelected ? <Check size={11} strokeWidth={3} /> : null}
              </span>
              {zoneName}
            </button>
          );
        })}
        {zoneNames.length === 0 ? (
          <p className={styles.fieldNote}>{emptyLabel}</p>
        ) : null}
      </div>
    </div>
  );
}

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/selectors",
)({
  component: RouteComponent,
});

type AppUser = {
  id: number;
  user_id: string;
  user_name?: string;
  active: boolean;
  created_at: string;
};

function generatePin(): string {
  return String(Math.floor(100000 + Math.random() * 900000));
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, "").slice(0, PIN_LENGTH);
}

/** AAA's `user_name` is optional -- a selector created before it existed, or without one set, falls back to the login id. */
function displayNameOf(user: AppUser): string {
  return user.user_name || user.user_id;
}

function initialsOf(displayName: string): string {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0]![0]! + words[1]![0]!).toUpperCase();
  }
  return displayName.slice(0, 2).toUpperCase();
}

function avatarColorOf(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash += userId.charCodeAt(i);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]!;
}

/**
 * openapi-react-query throws the parsed apiResponse body itself
 * (`{ code, message }`) rather than an `Error` -- see the analogous helper in
 * items.tsx.
 */
function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const body = error as { message?: unknown; error?: unknown };
    if (typeof body.message === "string" && body.message) return body.message;
    if (typeof body.error === "string" && body.error) return body.error;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * Shared by the row-level "User group"/"Validation" columns and the detail
 * drawer's own settings section -- both need the same per-user settings map,
 * and the row and drawer end up sharing one cache entry (same query key) once
 * both have been fetched for a given selector.
 */
function useSelectorSettings(
  orgId: string,
  warehouseId: string,
  userId: string,
) {
  return useQuery({
    queryKey: ["selector-settings", orgId, warehouseId, userId],
    queryFn: async () => {
      const { data, response } = await orgCfgFetchClient().GET(
        "/orgs/user_settings/get",
        { params: { query: { orgId, warehouseId, userId } } },
      );
      // A selector who has never had settings saved gets a 404 here -- that
      // is an empty settings map, not a failure.
      if (response.status === 404) return {};
      if (!data) throw new Error("Failed to load selector settings");
      return data.settings ?? {};
    },
  });
}

/**
 * Merges `patch` onto the selector's current settings and persists it.
 * Fetches the current settings first rather than trusting whatever the
 * caller already had in memory -- an `update` against a selector with no
 * settings record yet 404s, so a missing record falls back to `add`, and
 * merging (rather than overwriting) means this can save just the fields an
 * editing form owns without clobbering keys it doesn't know about (e.g.
 * `group`, which is only ever written from the User Groups page).
 */
async function saveSelectorSettings(
  orgId: string,
  warehouseId: string,
  userId: string,
  patch: Record<string, string>,
): Promise<void> {
  const { data, response } = await orgCfgFetchClient().GET(
    "/orgs/user_settings/get",
    { params: { query: { orgId, warehouseId, userId } } },
  );
  const exists = response.status !== 404;
  const nextSettings: Record<string, string> = {
    ...(exists ? data?.settings : {}),
    ...patch,
  };

  const { error } = await orgCfgFetchClient().POST(
    exists ? "/orgs/user_settings/update" : "/orgs/user_settings/add",
    { body: { orgId, warehouseId, userId, settings: nextSettings } },
  );
  if (error) throw error;
}

/**
 * A PIN this page's own form just set is known here in plaintext for exactly
 * one render -- create and reset-PIN both end in this one-time reveal of the
 * value that was just submitted, not a value read back from the server.
 */
function PinRevealPanel({
  userId,
  pin,
  title,
  body,
  doneLabel,
  onDone,
  t,
}: {
  userId: string;
  pin: string;
  title: string;
  body: string;
  doneLabel: string;
  onDone: () => void;
  t: TFunction;
}) {
  return (
    <div className={styles.reveal}>
      <h2 className={styles.modalTitle}>{title}</h2>
      <p className={styles.modalIntro}>{body}</p>
      <div className={styles.revealGrid}>
        <div>
          <span className={styles.revealLabel}>
            {t("selectors.table.userId")}
          </span>
          <span className={styles.revealValue}>{userId}</span>
        </div>
        <div>
          <span className={styles.revealLabel}>
            {t("selectors.create.pinLabel")}
          </span>
          <span className={`${styles.revealValue} ${styles.revealPin}`}>
            {pin}
          </span>
        </div>
      </div>
      <div className={styles.modalActions}>
        <Button
          className={styles.createButton}
          text={doneLabel}
          onPress={onDone}
        />
      </div>
    </div>
  );
}

/** What `handleCreate` needs beyond the AAA account itself -- see its own comment for why these are best-effort follow-ups rather than part of the account-creation failure path. */
type CreateSelectorExtras = {
  name: string;
  active: boolean;
  settings: Record<string, string>;
};

function CreateSelectorModal({
  isOpen,
  onOpenChange,
  onCreate,
  isPending,
  zoneNames,
  t,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (
    userId: string,
    pin: string,
    extras: CreateSelectorExtras,
  ) => Promise<void>;
  isPending: boolean;
  zoneNames: string[];
  t: TFunction;
}) {
  const [name, setName] = useState("");
  const [userId, setUserId] = useState("");
  const [pin, setPin] = useState("");
  const [active, setActive] = useState(true);
  const [zoneEligibility, setZoneEligibility] = useState<string[]>([]);
  const [taskType, setTaskType] = useState("All");
  const [band, setBand] = useState("B3");
  const [validationLevel, setValidationLevel] = useState("Standard");
  const [assignment, setAssignment] = useState("System-assigned");
  const [substitution, setSubstitution] = useState("No");
  const [created, setCreated] = useState<{
    userId: string;
    pin: string;
  } | null>(null);

  const toggleZone = (zoneName: string) => {
    setZoneEligibility((prev) =>
      prev.includes(zoneName)
        ? prev.filter((z) => z !== zoneName)
        : [...prev, zoneName],
    );
  };

  const reset = () => {
    setName("");
    setUserId("");
    setPin("");
    setActive(true);
    setZoneEligibility([]);
    setTaskType("All");
    setBand("B3");
    setValidationLevel("Standard");
    setAssignment("System-assigned");
    setSubstitution("No");
    setCreated(null);
  };

  // Routed through this rather than `onOpenChange` directly so the Cancel/Done
  // buttons reset state exactly like an Escape-key or backdrop dismissal does
  // -- otherwise a stale `created` (or any other field) from the last time
  // this modal was open survives and reappears the next time it opens.
  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) reset();
  };

  const close = () => handleOpenChange(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedId = userId.trim();
    if (isPending || !trimmedId || pin.length !== PIN_LENGTH) return;

    try {
      await onCreate(trimmedId, pin, {
        name: name.trim(),
        active,
        settings: {
          [ZONE_ELIGIBILITY_KEY]: encodeZoneEligibility(zoneEligibility),
          task_type_eligibility: taskType,
          difficulty_band_cap: band,
          validation_level: validationLevel,
          assignment,
          substitution_authorized: substitution,
        },
      });
      setCreated({ userId: trimmedId, pin });
    } catch {
      // onCreate already reported the failure via toast; keep the form open
      // so the admin can retry without re-entering values.
    }
  };

  return (
    <ModalOverlay
      className={styles.modalOverlay}
      isDismissable={!isPending}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
    >
      <RACModal className={styles.modal}>
        <Dialog
          className={styles.dialog}
          data-test-role="create-selector-modal"
        >
          {created ? (
            <PinRevealPanel
              userId={created.userId}
              pin={created.pin}
              title={t("selectors.create.successTitle")}
              body={t("selectors.create.successBody", {
                userId: created.userId,
              })}
              doneLabel={t("selectors.create.done")}
              onDone={close}
              t={t}
            />
          ) : (
            <form onSubmit={submit} className={styles.form}>
              <h2 className={styles.modalTitle}>
                {t("selectors.create.title")}
              </h2>
              <p className={styles.modalIntro}>{t("selectors.create.intro")}</p>
              <TextInput
                name="name"
                label={t("selectors.create.nameLabel")}
                placeholder={t("selectors.create.namePlaceholder")}
                value={name}
                onChange={(ev) => setName(ev.target.value)}
                autofocus={true}
              />
              <div className={styles.fieldRow}>
                <TextInput
                  name="user-id"
                  label={t("selectors.create.userIdLabel")}
                  placeholder={t("selectors.create.userIdPlaceholder")}
                  value={userId}
                  onChange={(ev) => setUserId(ev.target.value)}
                  isRequired={true}
                />
                <Select
                  name="status"
                  label={t("selectors.create.statusLabel")}
                  items={[
                    { id: "active", label: t("selectors.status.active") },
                    { id: "inactive", label: t("selectors.status.inactive") },
                  ]}
                  selectedId={active ? "active" : "inactive"}
                  onSelectionChange={(key) => setActive(key === "active")}
                />
              </div>
              <div className={styles.pinRow}>
                <TextInput
                  name="pin"
                  label={t("selectors.create.pinLabel")}
                  placeholder={t("selectors.create.pinPlaceholder")}
                  value={pin}
                  onChange={(ev) => setPin(digitsOnly(ev.target.value))}
                  isRequired={true}
                  maxLength={PIN_LENGTH}
                />
                <Button
                  type="button"
                  variant="secondary"
                  text={t("selectors.create.generate")}
                  onPress={() => setPin(generatePin())}
                />
              </div>

              <div className={styles.sectionDivider} />
              <div className={styles.drawerSectionHeading}>
                {t("selectors.create.settingsHeading")}
              </div>

              <ZonePillPicker
                zoneNames={zoneNames}
                selected={zoneEligibility}
                onToggle={toggleZone}
                label={t("selectors.detail.zoneEligibility")}
                emptyLabel={t("selectors.create.noZones")}
              />

              <div className={styles.fieldRow}>
                <Select
                  name="task-type-eligibility"
                  label={t("selectors.detail.taskTypeEligibility")}
                  items={selectItems(TASK_TYPE_OPTIONS)}
                  selectedId={taskType}
                  onSelectionChange={(key) => setTaskType(String(key))}
                />
                <Select
                  name="difficulty-band-cap"
                  label={t("selectors.detail.difficultyBandCap")}
                  items={selectItems(BAND_OPTIONS)}
                  selectedId={band}
                  onSelectionChange={(key) => setBand(String(key))}
                />
              </div>
              <div className={styles.fieldRow}>
                <Select
                  name="validation-level"
                  label={t("selectors.detail.validationLevel")}
                  items={selectItems(VALIDATION_OPTIONS)}
                  selectedId={validationLevel}
                  onSelectionChange={(key) => setValidationLevel(String(key))}
                />
                <Select
                  name="assignment"
                  label={t("selectors.detail.assignment")}
                  items={selectItems(ASSIGNMENT_OPTIONS)}
                  selectedId={assignment}
                  onSelectionChange={(key) => setAssignment(String(key))}
                />
              </div>
              <div className={styles.fieldRow}>
                <Select
                  name="substitution-authorized"
                  label={t("selectors.detail.substitutionAuthorized")}
                  items={selectItems(SUBSTITUTION_OPTIONS)}
                  selectedId={substitution}
                  onSelectionChange={(key) => setSubstitution(String(key))}
                />
              </div>

              <div className={styles.modalActions}>
                <Button
                  type="submit"
                  className={styles.createButton}
                  text={
                    isPending
                      ? t("selectors.create.submitting")
                      : t("selectors.create.submit")
                  }
                  isDisabled={
                    isPending || !userId.trim() || pin.length !== PIN_LENGTH
                  }
                />
                <Button
                  type="button"
                  variant="tertiary"
                  text={t("selectors.create.cancel")}
                  onPress={close}
                  isDisabled={isPending}
                />
              </div>
            </form>
          )}
        </Dialog>
      </RACModal>
    </ModalOverlay>
  );
}

function ResetPinModal({
  user,
  onOpenChange,
  onSubmit,
  isPending,
  t,
}: {
  user: AppUser | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (pin: string) => Promise<void>;
  isPending: boolean;
  t: TFunction;
}) {
  const [pin, setPin] = useState("");
  const [resetPin, setResetPin] = useState<string | null>(null);

  // Routed through this rather than `onOpenChange` directly so the
  // Cancel/Done buttons reset state exactly like an Escape-key or backdrop
  // dismissal does -- otherwise a stale `resetPin` from the last time this
  // modal was open survives and reappears the next time it opens.
  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      setPin("");
      setResetPin(null);
    }
  };

  const close = () => handleOpenChange(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isPending || pin.length !== PIN_LENGTH) return;

    try {
      await onSubmit(pin);
      setResetPin(pin);
    } catch {
      // onSubmit already reported the failure via toast; keep the form open.
    }
  };

  return (
    <ModalOverlay
      className={styles.modalOverlay}
      isDismissable={!isPending}
      isOpen={user !== null}
      onOpenChange={handleOpenChange}
    >
      <RACModal className={styles.modal}>
        <Dialog className={styles.dialog} data-test-role="reset-pin-modal">
          {user && resetPin ? (
            <PinRevealPanel
              userId={user.user_id}
              pin={resetPin}
              title={t("selectors.resetPin.successTitle")}
              body={t("selectors.resetPin.successBody", {
                userId: user.user_id,
              })}
              doneLabel={t("selectors.resetPin.done")}
              onDone={close}
              t={t}
            />
          ) : (
            <form onSubmit={submit} className={styles.form}>
              <h2 className={styles.modalTitle}>
                {t("selectors.resetPin.title")}
              </h2>
              <p className={styles.modalIntro}>
                {user
                  ? t("selectors.resetPin.intro", { userId: user.user_id })
                  : ""}
              </p>
              <div className={styles.pinRow}>
                <TextInput
                  name="new-pin"
                  label={t("selectors.resetPin.pinLabel")}
                  placeholder={t("selectors.create.pinPlaceholder")}
                  value={pin}
                  onChange={(ev) => setPin(digitsOnly(ev.target.value))}
                  isRequired={true}
                  maxLength={PIN_LENGTH}
                  autofocus={true}
                />
                <Button
                  type="button"
                  variant="secondary"
                  text={t("selectors.resetPin.generate")}
                  onPress={() => setPin(generatePin())}
                />
              </div>
              <div className={styles.modalActions}>
                <Button
                  type="submit"
                  className={styles.createButton}
                  text={
                    isPending
                      ? t("selectors.resetPin.submitting")
                      : t("selectors.resetPin.submit")
                  }
                  isDisabled={isPending || pin.length !== PIN_LENGTH}
                />
                <Button
                  type="button"
                  variant="tertiary"
                  text={t("selectors.resetPin.cancel")}
                  onPress={close}
                  isDisabled={isPending}
                />
              </div>
            </form>
          )}
        </Dialog>
      </RACModal>
    </ModalOverlay>
  );
}

/**
 * `group` is deliberately left out of the editable form below -- it's only
 * ever written from the User Groups page (so zones/users on the group side
 * stay in sync with it), so this drawer surfaces it read-only next to the
 * other identity fields instead.
 */
function SelectorDetailDrawer({
  user,
  orgId,
  warehouseId,
  zoneNames,
  onClose,
  onSave,
  isSaving,
  t,
}: {
  user: AppUser;
  orgId: string;
  warehouseId: string;
  zoneNames: string[];
  onClose: () => void;
  onSave: (userId: string, patch: Record<string, string>) => Promise<void>;
  isSaving: boolean;
  t: TFunction;
}) {
  const statusLabel = user.active
    ? t("selectors.status.active")
    : t("selectors.status.inactive");

  const settingsQuery = useSelectorSettings(orgId, warehouseId, user.user_id);

  const settings = settingsQuery.data ?? {};

  const [initialized, setInitialized] = useState(false);
  const [zoneEligibility, setZoneEligibility] = useState<string[]>([]);
  const [taskType, setTaskType] = useState("All");
  const [band, setBand] = useState("B3");
  const [validationLevel, setValidationLevel] = useState("Standard");
  const [assignment, setAssignment] = useState("System-assigned");
  const [substitution, setSubstitution] = useState("No");

  // Seeds the form from the fetched settings exactly once -- after that, the
  // fields are this form's own state, so a background refetch (e.g. after
  // save) doesn't stomp on what the admin is mid-way through editing.
  useEffect(() => {
    if (initialized || !settingsQuery.isSuccess) return;
    setZoneEligibility(decodeZoneEligibility(settings[ZONE_ELIGIBILITY_KEY]));
    setTaskType(settings.task_type_eligibility || "All");
    setBand(settings.difficulty_band_cap || "B3");
    setValidationLevel(settings.validation_level || "Standard");
    setAssignment(settings.assignment || "System-assigned");
    setSubstitution(settings.substitution_authorized || "No");
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once, see comment above
  }, [initialized, settingsQuery.isSuccess]);

  const toggleZone = (zoneName: string) => {
    setZoneEligibility((prev) =>
      prev.includes(zoneName)
        ? prev.filter((z) => z !== zoneName)
        : [...prev, zoneName],
    );
  };

  const handleSave = async () => {
    try {
      await onSave(user.user_id, {
        [ZONE_ELIGIBILITY_KEY]: encodeZoneEligibility(zoneEligibility),
        task_type_eligibility: taskType,
        difficulty_band_cap: band,
        validation_level: validationLevel,
        assignment,
        substitution_authorized: substitution,
      });
    } catch {
      // onSave already reported the failure via toast; keep the drawer open
      // so the admin can retry without re-entering values.
    }
  };

  return (
    <div
      className={styles.drawerBackdrop}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={styles.drawer}
        role="dialog"
        aria-label={displayNameOf(user)}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.drawerHeader}>
          <span
            className={styles.drawerAvatar}
            style={{ backgroundColor: avatarColorOf(user.user_id) }}
          >
            {initialsOf(displayNameOf(user))}
          </span>
          <div className={styles.drawerHeaderText}>
            <div className={styles.drawerTitleRow}>
              <h2>{displayNameOf(user)}</h2>
              <Tag
                label={statusLabel}
                display={{ tone: user.active ? "success" : "neutral" }}
              />
            </div>
            <span>
              {t("selectors.detail.subtitleRole")} ·{" "}
              <DateValue dateValue={user.created_at} />
            </span>
          </div>
          <button
            type="button"
            className={styles.drawerClose}
            aria-label={t("selectors.detail.close")}
            onClick={onClose}
          >
            <X size={18} aria-hidden={true} />
          </button>
        </div>

        <div className={styles.drawerSection}>
          <div className={styles.drawerSectionHeading}>
            {t("selectors.detail.identityHeading")}
          </div>
          <dl className={styles.drawerGrid}>
            <div>
              <dt>{t("selectors.table.userId")}</dt>
              <dd>{user.user_id}</dd>
            </div>
            <div>
              <dt>{t("selectors.table.status")}</dt>
              <dd>{statusLabel}</dd>
            </div>
            <div>
              <dt>{t("selectors.table.created")}</dt>
              <dd>
                <DateValue dateValue={user.created_at} />
              </dd>
            </div>
            <div>
              <dt>{t("selectors.table.userGroup")}</dt>
              <dd>
                {settings.group || (
                  <Tag
                    label={t("selectors.table.notAssigned")}
                    display={{ tone: "warning" }}
                  />
                )}
              </dd>
            </div>
          </dl>
        </div>

        <div className={styles.drawerSection}>
          <div className={styles.drawerSectionHeading}>
            {t("selectors.detail.settingsHeading")}
          </div>
          {settingsQuery.isLoading ? (
            <Loader type="inline" />
          ) : settingsQuery.isError ? (
            <p className={styles.drawerSettingsError}>
              {t("selectors.detail.settingsFailed")}
            </p>
          ) : (
            <div className={styles.form}>
              <ZonePillPicker
                zoneNames={zoneNames}
                selected={zoneEligibility}
                onToggle={toggleZone}
                label={t("selectors.detail.zoneEligibility")}
                emptyLabel={t("selectors.create.noZones")}
              />
              <div className={styles.fieldRow}>
                <Select
                  name="task-type-eligibility"
                  label={t("selectors.detail.taskTypeEligibility")}
                  items={selectItems(TASK_TYPE_OPTIONS)}
                  selectedId={taskType}
                  onSelectionChange={(key) => setTaskType(String(key))}
                />
                <Select
                  name="difficulty-band-cap"
                  label={t("selectors.detail.difficultyBandCap")}
                  items={selectItems(BAND_OPTIONS)}
                  selectedId={band}
                  onSelectionChange={(key) => setBand(String(key))}
                />
              </div>
              <div className={styles.fieldRow}>
                <Select
                  name="validation-level"
                  label={t("selectors.detail.validationLevel")}
                  items={selectItems(VALIDATION_OPTIONS)}
                  selectedId={validationLevel}
                  onSelectionChange={(key) => setValidationLevel(String(key))}
                />
                <Select
                  name="assignment"
                  label={t("selectors.detail.assignment")}
                  items={selectItems(ASSIGNMENT_OPTIONS)}
                  selectedId={assignment}
                  onSelectionChange={(key) => setAssignment(String(key))}
                />
              </div>
              <div className={styles.fieldRow}>
                <Select
                  name="substitution-authorized"
                  label={t("selectors.detail.substitutionAuthorized")}
                  items={selectItems(SUBSTITUTION_OPTIONS)}
                  selectedId={substitution}
                  onSelectionChange={(key) => setSubstitution(String(key))}
                />
              </div>
            </div>
          )}
        </div>

        <div className={styles.drawerFooter}>
          <Button
            type="button"
            className={styles.createButton}
            text={
              isSaving
                ? t("selectors.detail.saving")
                : t("selectors.detail.save")
            }
            onPress={handleSave}
            isDisabled={
              isSaving || settingsQuery.isLoading || settingsQuery.isError
            }
          />
          <Button
            type="button"
            variant="tertiary"
            text={t("selectors.detail.cancel")}
            onPress={onClose}
            isDisabled={isSaving}
          />
        </div>
      </div>
    </div>
  );
}

type TeamQrCode = { userId: string; dataUrl: string };

/**
 * Mirrors nextgen_config's `printQRCodes()` -- a print-only copy of the grid
 * rendered straight into `document.body` (outside the modal's overlay/portal
 * stack) so `window.print()` has something printable to find: the overlay
 * itself is `position: fixed` and clipped/scrolling, which prints as a blank
 * or cut-off page in every browser we support. This copy stays hidden except
 * under `@media print`, where the rest of the page is hidden in its place --
 * see `.qrPrintArea` in selectors.module.css.
 */
function TeamQrPrintArea({ qrCodes }: { qrCodes: TeamQrCode[] }) {
  return createPortal(
    <div className={styles.qrPrintArea}>
      <div className={styles.qrPrintGrid}>
        {qrCodes.map((qrCode) => (
          <div key={qrCode.userId} className={styles.qrPrintCard}>
            <img className={styles.qrPrintImage} src={qrCode.dataUrl} alt="" />
            <span className={styles.qrPrintId}>{qrCode.userId}</span>
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Fetches every selector's QR code image (already rendered server-side by
 * orgcfg's `/orgs/user_groups/qr_codes`) and renders a printable grid --
 * no PIN material ever reaches this page, just the finished images.
 */
function TeamQrCodesModal({
  isOpen,
  onClose,
  orgId,
  warehouseId,
  t,
}: {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  warehouseId: string;
  t: TFunction;
}) {
  const qrQuery = useQuery({
    queryKey: ["team-qr-codes", orgId, warehouseId],
    queryFn: async () => {
      const { data } = await orgCfgFetchClient().GET(
        "/orgs/user_groups/qr_codes",
        { params: { query: { orgId, warehouseId, appName: APP_NAME } } },
      );
      if (!data) throw new Error("Failed to load team QR codes");
      return data.qrCodes;
    },
    enabled: isOpen,
  });

  const qrCodes = qrQuery.data ?? [];

  return (
    <>
      {isOpen ? <TeamQrPrintArea qrCodes={qrCodes} /> : null}
      <ModalOverlay
        className={styles.modalOverlay}
        isDismissable={true}
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <RACModal className={styles.qrModal}>
          <Dialog className={styles.qrDialog} data-test-role="team-qr-modal">
            <div className={styles.qrHeader}>
              <h2 className={styles.modalTitle}>
                {t("selectors.teamQr.title")}
              </h2>
              <div className={styles.qrHeaderActions}>
                <RACButton
                  className={styles.qrPrintButton}
                  aria-label={t("selectors.teamQr.print")}
                  isDisabled={qrCodes.length === 0}
                  onPress={() => window.print()}
                  data-test-role="team-qr-print"
                >
                  <Printer size={16} aria-hidden={true} />
                  {t("selectors.teamQr.print")}
                </RACButton>
                <RACButton
                  className={styles.modalDismiss}
                  aria-label={t("selectors.teamQr.close")}
                  onPress={onClose}
                >
                  <X size={16} aria-hidden={true} />
                </RACButton>
              </div>
            </div>
            <p className={styles.modalIntro}>{t("selectors.teamQr.intro")}</p>

            {qrQuery.isLoading ? (
              <Loader type="page" />
            ) : qrQuery.isError ? (
              <AppError
                description={t("selectors.teamQr.failed")}
                resolution={t("selectors.failureSupport")}
              />
            ) : qrCodes.length === 0 ? (
              <EmptyState heading={t("selectors.empty")} />
            ) : (
              <div className={styles.qrGrid}>
                {qrCodes.map((qrCode) => (
                  <div
                    key={qrCode.userId}
                    className={styles.qrCard}
                    data-test-role="qr-card"
                  >
                    <img
                      className={styles.qrCardImage}
                      src={qrCode.dataUrl}
                      alt={t("selectors.teamQr.cardLabel", {
                        userId: qrCode.userId,
                      })}
                    />
                    <span className={styles.qrCardId}>{qrCode.userId}</span>
                  </div>
                ))}
              </div>
            )}
          </Dialog>
        </RACModal>
      </ModalOverlay>
    </>
  );
}

function SelectorTableRow({
  user,
  orgId,
  warehouseId,
  isTogglePending,
  onEdit,
  onResetPin,
  onToggle,
  t,
}: {
  user: AppUser;
  orgId: string;
  warehouseId: string;
  isTogglePending: boolean;
  onEdit: () => void;
  onResetPin: () => void;
  onToggle: () => void;
  t: TFunction;
}) {
  const settingsQuery = useSelectorSettings(orgId, warehouseId, user.user_id);
  const settings = settingsQuery.data ?? {};
  const zoneEligibility = decodeZoneEligibility(settings[ZONE_ELIGIBILITY_KEY]);

  return (
    <TableRow data-test-role="selector-row">
      <TableCell>
        <div className={styles.userCell}>
          <span
            className={styles.avatar}
            style={{ backgroundColor: avatarColorOf(user.user_id) }}
          >
            {initialsOf(displayNameOf(user))}
          </span>
          <span className={styles.userName}>{displayNameOf(user)}</span>
        </div>
      </TableCell>
      <TableCell>
        <span className={styles.userId}>{user.user_id}</span>
      </TableCell>
      <TableCell>
        <Tag
          label={
            user.active
              ? t("selectors.status.active")
              : t("selectors.status.inactive")
          }
          display={{ tone: user.active ? "success" : "neutral" }}
        />
      </TableCell>
      <TableCell>
        {zoneEligibility.length > 0 ? (
          <span>{zoneEligibility.join(", ")}</span>
        ) : (
          <span className={styles.placeholder}>—</span>
        )}
      </TableCell>
      <TableCell>
        {settings.group ? (
          <span>{settings.group}</span>
        ) : (
          <Tag
            label={t("selectors.table.notAssigned")}
            display={{ tone: "warning" }}
          />
        )}
      </TableCell>
      <TableCell>
        {settings.validation_level ? (
          <span
            className={styles.validationChip}
            style={{
              color: validationColor(settings.validation_level),
              backgroundColor: `${validationColor(settings.validation_level)}1e`,
            }}
          >
            {settings.validation_level}
          </span>
        ) : (
          <span className={styles.placeholder}>—</span>
        )}
      </TableCell>
      <TableCell>
        <div className={styles.rowActions}>
          <button
            type="button"
            className={`${styles.rowActionButton} ${styles.rowActionIcon}`}
            aria-label={t("selectors.rowActions.edit")}
            onClick={onEdit}
            data-test-role="selector-edit"
          >
            <Edit2 size={14} aria-hidden={true} />
          </button>
          <button
            type="button"
            className={styles.rowActionButton}
            onClick={onResetPin}
            data-test-role="selector-reset-pin"
          >
            {t("selectors.rowActions.resetPin")}
          </button>
          <button
            type="button"
            className={`${styles.rowActionButton} ${
              user.active
                ? styles.rowActionDeactivate
                : styles.rowActionReactivate
            }`}
            disabled={isTogglePending}
            onClick={onToggle}
            data-test-role="selector-toggle-status"
          >
            {user.active
              ? t("selectors.rowActions.deactivate")
              : t("selectors.rowActions.activate")}
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function RouteComponent() {
  const params = Route.useParams();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  const client = useMemo(() => apiClient(), []);
  const issClient = useMemo(() => issApiClient(), []);
  const queryClient = useQueryClient();

  const orgId = AuthUtils.getOrgIdByStub(authState, params.org) ?? params.org;
  const warehouseId = AuthUtils.getWarehouseId(
    authState,
    orgId,
    params.location,
  );
  const idsReady = typeof warehouseId === "string";

  // Zone eligibility is secondary to the selector directory itself, so a
  // failure here doesn't block the page -- the picker just renders with no
  // zones to offer (see `zoneNames` below).
  const zonesQuery = issClient.useQuery("get", "/zones/all", {});
  const zoneNames = ((zonesQuery.data?.data ?? []) as { name: string }[])
    .map((zone) => zone.name)
    .sort((a, b) => a.localeCompare(b));

  const listQuery = client.useQuery(
    "get",
    "/app_user/list",
    {
      params: {
        query: {
          org_id: orgId,
          warehouse_id: warehouseId ?? "",
          app_name: APP_NAME,
        },
      },
    },
    { enabled: idsReady },
  );

  const [qrOpen, setQrOpen] = useState(false);

  const createMutation = client.useMutation("post", "/app_user/create");
  const updateMutation = client.useMutation("post", "/app_user/update");
  const resetPinMutation = client.useMutation("post", "/app_user/reset_pin");

  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<AppUser | null>(null);
  const [editTarget, setEditTarget] = useState<AppUser | null>(null);
  const [statusPendingId, setStatusPendingId] = useState<number | null>(null);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const users = (listQuery.data ?? []) as AppUser[];
  const trimmed = q.trim().toLowerCase();
  const filtered = trimmed
    ? users.filter((u) => u.user_id.toLowerCase().includes(trimmed))
    : users;
  const sorted = [...filtered].sort((a, b) =>
    a.user_id.localeCompare(b.user_id),
  );

  const openTeamQr = () => setQrOpen(true);

  const closeTeamQr = () => setQrOpen(false);

  const handleCreate = async (
    userId: string,
    pin: string,
    extras: CreateSelectorExtras,
  ) => {
    try {
      await createMutation.mutateAsync({
        body: {
          app_name: APP_NAME,
          org_id: orgId,
          warehouse_id: warehouseId!,
          user_id: userId,
          pin,
          ...(extras.name ? { user_name: extras.name } : {}),
        },
      });
      logger.info("Created selector", { userId });
    } catch (error) {
      logger.warn("Failed to create selector", { error, userId });
      toast.error(errorMessage(error, t("selectors.create.failedFallback")));
      throw error;
    }

    // Best-effort follow-ups: the account already exists at this point, so a
    // failure here shouldn't reopen the form or block the PIN reveal -- just
    // warn, since the admin can still fix status/settings from the row and
    // the edit drawer afterward.
    if (!extras.active) {
      try {
        await updateMutation.mutateAsync({
          body: {
            app_name: APP_NAME,
            org_id: orgId,
            warehouse_id: warehouseId!,
            user_id: userId,
            active: false,
          },
        });
      } catch (error) {
        logger.warn("Failed to deactivate newly created selector", {
          error,
          userId,
        });
        toast.error(t("selectors.create.statusFailed", { userId }));
      }
    }

    try {
      const { error } = await orgCfgFetchClient().POST(
        "/orgs/user_settings/add",
        {
          body: {
            orgId,
            warehouseId: warehouseId!,
            userId,
            settings: extras.settings,
          },
        },
      );
      if (error) throw error;
    } catch (error) {
      logger.warn("Failed to save new selector's settings", {
        error,
        userId,
      });
      toast.error(t("selectors.create.settingsFailed", { userId }));
    }

    listQuery.refetch();
  };

  const handleResetPin = async (pin: string) => {
    if (!resetTarget) return;
    const userId = resetTarget.user_id;
    try {
      await resetPinMutation.mutateAsync({
        body: {
          app_name: APP_NAME,
          org_id: orgId,
          warehouse_id: warehouseId!,
          user_id: userId,
          new_pin: pin,
        },
      });
      logger.info("Reset selector PIN", { userId });
    } catch (error) {
      logger.warn("Failed to reset selector PIN", { error, userId });
      toast.error(errorMessage(error, t("selectors.resetPin.failedFallback")));
      throw error;
    }
  };

  const handleToggleStatus = (user: AppUser) => {
    if (updateMutation.isPending) return;
    const nextActive = !user.active;

    setStatusPendingId(user.id);
    updateMutation
      .mutateAsync({
        body: {
          app_name: APP_NAME,
          org_id: orgId,
          warehouse_id: warehouseId!,
          user_id: user.user_id,
          active: nextActive,
        },
      })
      .then(() => {
        logger.info("Updated selector status", {
          userId: user.user_id,
          active: nextActive,
        });
        toast.success(
          t(
            nextActive
              ? "selectors.toast.activated"
              : "selectors.toast.deactivated",
            { userId: user.user_id },
          ),
        );
        listQuery.refetch();
      })
      .catch((error: unknown) => {
        logger.warn("Failed to update selector status", {
          error,
          userId: user.user_id,
        });
        toast.error(
          errorMessage(
            error,
            t("selectors.toast.statusFailed", { userId: user.user_id }),
          ),
        );
      })
      .finally(() => setStatusPendingId(null));
  };

  const handleSaveSettings = async (
    userId: string,
    patch: Record<string, string>,
  ) => {
    setIsSavingSettings(true);
    try {
      await saveSelectorSettings(orgId, warehouseId!, userId, patch);
      logger.info("Updated selector settings", { userId });
      toast.success(t("selectors.detail.saveSuccessToast", { userId }));
      queryClient.invalidateQueries({
        queryKey: ["selector-settings", orgId, warehouseId, userId],
      });
      setEditTarget(null);
    } catch (error) {
      logger.warn("Failed to update selector settings", { error, userId });
      toast.error(
        errorMessage(error, t("selectors.detail.saveFailedFallback")),
      );
      throw error;
    } finally {
      setIsSavingSettings(false);
    }
  };

  return (
    <div className={palletOverrides.basicPageContent}>
      <div className={styles.page}>
        <header className={styles.pageHeader}>
          <div className={styles.titleRow}>
            <h1>{t("selectors.heading")}</h1>
            <div className={styles.toolbar}>
              <SearchField
                aria-label={t("selectors.filters.searchAriaLabel")}
                className={styles.searchField}
                value={q}
                onChange={setQ}
              >
                <SearchIcon
                  size={15}
                  className={styles.searchIcon}
                  aria-hidden={true}
                />
                <Input placeholder={t("selectors.filters.searchPlaceholder")} />
                {q ? (
                  <RACButton
                    className={styles.searchClear}
                    onPress={() => setQ("")}
                  >
                    <X size={13} aria-hidden={true} />
                  </RACButton>
                ) : null}
              </SearchField>

              <RACButton className={styles.qrTrigger} onPress={openTeamQr}>
                <Grid size={15} aria-hidden={true} />
                {t("selectors.teamQr.button")}
              </RACButton>

              <Button
                className={styles.createButton}
                icon={{ type: "plus" }}
                text={t("selectors.createButton")}
                onPress={() => setCreateOpen(true)}
              />
            </div>
          </div>
          <p>{t("selectors.subheading")}</p>
        </header>

        <div className={styles.tableWrap}>
          {!idsReady || listQuery.isLoading ? (
            <Loader type="page" />
          ) : listQuery.isError ? (
            <AppError
              description={t("selectors.failed")}
              resolution={t("selectors.failureSupport")}
            />
          ) : (
            <Table
              aria-label={t("selectors.table.ariaLabel")}
              isResizable={false}
            >
              <TableHead>
                <TableHeaderCell isRowHeader={true}>
                  {t("selectors.table.user")}
                </TableHeaderCell>
                <TableHeaderCell>{t("selectors.table.userId")}</TableHeaderCell>
                <TableHeaderCell>{t("selectors.table.status")}</TableHeaderCell>
                <TableHeaderCell>
                  {t("selectors.table.zoneEligibility")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("selectors.table.userGroup")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("selectors.table.validation")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("selectors.table.actions")}
                </TableHeaderCell>
              </TableHead>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <EmptyState heading={t("selectors.empty")} />
                    </TableCell>
                  </TableRow>
                ) : (
                  sorted.map((user) => (
                    <SelectorTableRow
                      key={user.id}
                      user={user}
                      orgId={orgId}
                      warehouseId={warehouseId ?? ""}
                      isTogglePending={statusPendingId === user.id}
                      onEdit={() => setEditTarget(user)}
                      onResetPin={() => setResetTarget(user)}
                      onToggle={() => handleToggleStatus(user)}
                      t={t}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <CreateSelectorModal
        isOpen={createOpen}
        onOpenChange={setCreateOpen}
        onCreate={handleCreate}
        isPending={createMutation.isPending}
        zoneNames={zoneNames}
        t={t}
      />

      <ResetPinModal
        user={resetTarget}
        onOpenChange={(open) => {
          if (!open) setResetTarget(null);
        }}
        onSubmit={handleResetPin}
        isPending={resetPinMutation.isPending}
        t={t}
      />

      <TeamQrCodesModal
        isOpen={qrOpen}
        onClose={closeTeamQr}
        orgId={orgId}
        warehouseId={warehouseId ?? ""}
        t={t}
      />

      {editTarget ? (
        <SelectorDetailDrawer
          user={editTarget}
          orgId={orgId}
          warehouseId={warehouseId ?? ""}
          zoneNames={zoneNames}
          onClose={() => setEditTarget(null)}
          onSave={handleSaveSettings}
          isSaving={isSavingSettings}
          t={t}
        />
      ) : null}
    </div>
  );
}
